import React, { useCallback, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@mui/material'

/* Actions */
import { useDashboardActions } from 'redux/actions/Dashboards'
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'

import { addRef } from 'utils/queryHandler'

/* Hooks */
import { useDashboardsInGroup } from 'redux/hooks/Dashboards'
import { useAllowEditDashboard } from 'redux/hooks/Permissions'
import { useTranslation } from 'language/translator'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

/* Molecules */
import OptionsMenu from 'components_new/molecules/OptionsMenu'
import TabSwitch from 'components_new/molecules/TabSwitch'

/* Organisms */
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import EditTitleDialog from 'components_new/organisms/dialogs/EditTitleDialog'
import EmbedDashboardDialog from 'components_new/organisms/dialogs/EmbedDashboardDialog'

import CopyOrMoveDashboardDialog from './dialogs/CopyOrMoveDashboardDialog'

import Loading from './loading'
import { useDashboardTabs, TDialogType } from './utils'

/* Types */
import { TDashboard } from 'types/GlobalDashboard'
import { TDashboardGroup } from 'types/GlobalDashboardGroup'
import { Context } from 'types/GlobalUserEvents'

interface DashboardTabsProps {
  dashboard: TDashboard | null
  dashboardGroup: TDashboardGroup | null
}

const DashboardTabs = (props: DashboardTabsProps) => {
  const { dashboard, dashboardGroup } = props

  const { tryUpdateDashboardOrder } = useDashboardGroupActions()
  const { tryCreateDashboard, tryDeleteDashboard, tryUpdateDashboard } =
    useDashboardActions()

  const allowEditDashboard = useAllowEditDashboard(dashboard?.id || null)
  const { t } = useTranslation()
  const history = useHistory()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const dashboards = useDashboardsInGroup(dashboardGroup?.id)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'))

  /*-- dialogs --*/
  const [dialog, setDialog] = useState<{
    type: TDialogType | null
    id: string | null
  }>({
    type: null,
    id: null
  })

  const onDuplicate = useCallback((dashboardId: string) => {
    tryCreateDashboard(
      {
        data: {
          dashboard_id: dashboardId
        }
      },
      (createdId) =>
        history.push(addRef(`/dashboards/${createdId}`, Context.DUPLICATE))
    )
  }, [])

  const onMove = useCallback(
    (toMove: TDashboard, dir: number) => {
      if (!dashboardGroup) return

      const currentIndex = toMove.index
      const newIndex = currentIndex + dir

      // update affected dashboard
      const affectedDashboard = dashboards.find(
        (dashboard) => dashboard.index === newIndex
      )

      const newOrder = dashboards.map((db) => {
        if (db.id === toMove.id) {
          return { ...db, index: newIndex }
        }

        if (affectedDashboard?.id === db.id) {
          return { ...db, index: currentIndex }
        }

        return db
      })

      tryUpdateDashboardOrder(
        dashboardGroup.id,
        newOrder.sort((a, b) => a.index - b.index)
      )
    },
    [dashboardGroup, dashboards]
  )

  const handleOpenDialog = useCallback(
    (dashboardId: string, type: TDialogType) => {
      setDialog({ type, id: dashboardId })
    },
    []
  )

  const tabs = useDashboardTabs(
    dashboards,
    allowEditDashboard,
    lgUp,
    onMove,
    onDuplicate,
    handleOpenDialog
  )

  if (isMobile) {
    return null
  }

  const handleCloseDialog = () => {
    setDialog({ type: null, id: null })
  }

  const handleOpenTab = (dashboardId: string) => {
    history.push(
      addRef(`/dashboards/${dashboardId}`, Context.NAVIGATE_TO_NEW_TAB)
    )
  }

  /*-- functions --*/
  const onAddTab = () => {
    if (!dashboardGroup) return

    tryCreateDashboard(
      {
        data: {
          dashboard_group_id: dashboardGroup.id
        }
      },
      (id) =>
        history.push(
          addRef(
            `/dashboards/${id}?new_dashboard=true`,
            Context.NAVIGATE_TO_NEW_TAB
          )
        )
    )
  }

  const open = Boolean(anchorEl)

  if (!dashboard || !dashboardGroup) return <Loading />

  return (
    <>
      <Box sx={{ display: 'block' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/*-- show all --*/}
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            size="small"
          >
            <Icon name="UnfoldMore" />
          </IconButton>

          <OptionsMenu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            onClose={() => setAnchorEl(null)}
            open={open}
            options={[
              ...tabs.map((tab) => {
                return {
                  disabled: tab.disabled,
                  onClick: () => handleOpenTab(tab.value),
                  title: tab.label as string
                }
              })
            ]}
          />

          {/*-- add --*/}
          {allowEditDashboard ? (
            <Box>
              <Tooltip title={t('dashboards', 'add_tab')}>
                <IconButton onClick={() => onAddTab()} size="small">
                  <Icon name="AddOutlined" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}

          <TabSwitch
            color="primary"
            options={tabs}
            value={dashboard.id}
            onChange={handleOpenTab}
            noGutter={true}
            maxHeight="40px"
          />
        </Box>
      </Box>

      {/*-- dialogs --*/}

      {/*-- delete dashboard --*/}
      <DeleteDialog
        onClose={handleCloseDialog}
        onDelete={(callback: () => void) => {
          if (dialog.id) {
            tryDeleteDashboard(dialog.id, () => {
              if (dialog.id === dashboard.id) {
                // removing current dashboard --> navigate to first
                const nextDashboard = dashboards.filter(
                  (db) => db.id !== dialog.id
                )[0]

                history.push(
                  addRef(
                    `/dashboards/${nextDashboard.id}`,
                    Context.NAVIGATE_TO_NEW_TAB
                  )
                )
              }
              handleCloseDialog()
              callback()
            })
          }
        }}
        open={dialog.id !== null && dialog.type === 'delete_dashboard'}
        title={tabs.find((item) => item.value === dialog.id)?.title}
        type="flik"
      />

      {/*-- edit dashboard title --*/}
      <EditTitleDialog
        onClose={handleCloseDialog}
        onSubmit={(value) => {
          if (dialog.id) {
            tryUpdateDashboard(dialog.id, { data: { title: value } })
          }
        }}
        open={dialog.id !== null && dialog.type === 'edit_dashboard_title'}
        title={tabs.find((item) => item.value === dialog.id)?.title}
      />

      {/*-- copy or move dashboard --*/}
      <CopyOrMoveDashboardDialog
        id={dialog.id}
        onClose={handleCloseDialog}
        open={
          dialog.id !== null &&
          (dialog.type === 'copy_dashboard' || dialog.type === 'move_dashboard')
        }
        type={dialog.type === 'move_dashboard' ? 'move' : 'copy'}
      />

      <EmbedDashboardDialog
        id={dialog.id}
        dashboardGroupFilters={dashboardGroup?.dashboard_filters ?? []}
        onClose={handleCloseDialog}
        open={dialog.id !== null && dialog.type === 'embed_dashboard'}
      />
    </>
  )
}

export default DashboardTabs
